/**
 * TopbarSlot — lets a routed view put its own control into the topbar.
 *
 * The topbar is rendered once by the layout, well above the router
 * Outlet, so a page cannot reach it by nesting. This hands the page a
 * portal instead: `<TopbarSlot>` renders its children into the div the
 * Header parks in the bar, and tells the Header the slot is occupied so
 * the global search steps aside for as long as the page is mounted.
 *
 * Because it is a portal and not a stored element, the slotted control
 * re-renders with the page that owns it — no stale closures over the
 * page's state.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const TopbarSlotContext = createContext(null);

export const TopbarSlotProvider = ({ children }) => {
  // The DOM node the Header hands us, and how many views are filling it.
  const [el, setEl] = useState(null);
  const [claims, setClaims] = useState(0);

  const claim = useCallback(() => {
    setClaims((n) => n + 1);
    return () => setClaims((n) => Math.max(0, n - 1));
  }, []);

  const value = useMemo(
    () => ({ el, setEl, claim, filled: claims > 0 }),
    [el, claim, claims]
  );

  return <TopbarSlotContext.Provider value={value}>{children}</TopbarSlotContext.Provider>;
};

/** Header-side hook: the ref to attach, and whether a page is using the slot. */
export const useTopbarSlotHost = () => useContext(TopbarSlotContext);

/** Page-side component: renders `children` into the topbar. */
const TopbarSlot = ({ children }) => {
  const ctx = useContext(TopbarSlotContext);
  const claim = ctx?.claim;

  useEffect(() => (claim ? claim() : undefined), [claim]);

  if (!ctx?.el) return null;
  return createPortal(children, ctx.el);
};

export default TopbarSlot;
